// src/components/admin/AuditLogsTab.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ComputerIcon from '@mui/icons-material/Computer';
import CategoryIcon from '@mui/icons-material/Category';
import { useTranslation } from 'react-i18next';
import { getAuditLogs, getAuditLogStatistics, exportAuditLogs } from '../../services/adminService';
import { AuditLogsResponse, AuditLogStatisticsResponse, AuditLog, AuditLogFilters } from '../../types/admin';

const AuditLogsTab: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<AuditLogsResponse | null>(null);
  const [statistics, setStatistics] = useState<AuditLogStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchData();
    fetchStatistics();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, search, categoryFilter, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: AuditLogFilters = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      if (search) filters.search = search;
      if (categoryFilter) filters.category = categoryFilter;
      if (statusFilter) filters.status = statusFilter as 'success' | 'failed';

      const response = await getAuditLogs(filters);
      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch audit logs:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await getAuditLogStatistics(30);
      setStatistics(response);
    } catch (err) {
      console.error('Failed to fetch audit log statistics:', err);
    }
  };

  const handleExport = async () => {
    try {
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      const blob = await exportAuditLogs(
        thirtyDaysAgo.toISOString().split('T')[0],
        today.toISOString().split('T')[0]
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${today.toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      console.error('Failed to export audit logs:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionStyle = (action: string) => {
    if (action.includes('CREATED') || action.includes('APPROVED')) {
      return { color: '#16a34a', bgColor: '#dcfce7' };
    }
    if (action.includes('DELETED') || action.includes('BANNED') || action.includes('REJECTED')) {
      return { color: '#dc2626', bgColor: '#fef2f2' };
    }
    if (action.includes('UPDATED')) {
      return { color: '#d97706', bgColor: '#fef3c7' };
    }
    return { color: '#64748b', bgColor: '#f1f5f9' };
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'user': return <PersonIcon sx={{ fontSize: 16 }} />;
      default: return <CategoryIcon sx={{ fontSize: 16 }} />;
    }
  };

  // Calculate category distribution for stats
  const totalByCategory = statistics?.by_category
    ? Object.values(statistics.by_category).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            {t('auditLogs.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Historique des actions administratives
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          sx={{ borderRadius: 1, borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#2563eb', color: '#2563eb' } }}
        >
          {t('auditLogs.export')}
        </Button>
      </Stack>

      {/* Stats summary */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {t('auditLogs.totalLogs')}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                      {statistics.total_logs.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      30 derniers jours
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 1 }}>
                    <HistoryIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={9}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                  Par catégorie
                </Typography>
                <Stack spacing={1.5}>
                  {Object.entries(statistics.by_category).slice(0, 4).map(([cat, count]) => {
                    const percentage = totalByCategory > 0 ? (count / totalByCategory) * 100 : 0;
                    return (
                      <Box key={cat}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={500} color="#1e293b" sx={{ textTransform: 'capitalize' }}>
                            {cat}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="#64748b">
                            {count}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{ height: 4, borderRadius: 0.5, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#2563eb' } }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              placeholder={t('auditLogs.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('auditLogs.category')}</InputLabel>
              <Select
                value={categoryFilter}
                label={t('auditLogs.category')}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">{t('auditLogs.all')}</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="verification">Verification</MenuItem>
                <MenuItem value="report">Report</MenuItem>
                <MenuItem value="moderation">Moderation</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('auditLogs.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('auditLogs.status')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">{t('auditLogs.all')}</MenuItem>
                <MenuItem value="success">{t('auditLogs.success')}</MenuItem>
                <MenuItem value="failed">{t('auditLogs.failed')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('auditLogs.dateTime')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('auditLogs.admin')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('auditLogs.action')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('auditLogs.category')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('auditLogs.target')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('auditLogs.status')}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0', width: 60 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.logs && data.logs.length > 0 ? (
                    data.logs.map((log) => {
                      const actionStyle = getActionStyle(log.action);
                      return (
                        <TableRow
                          key={log.id}
                          sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" color="#64748b">
                              {formatDate(log.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: '#2563eb', fontSize: 11 }}>
                                {log.admin_name?.charAt(0).toUpperCase() || 'A'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500} color="#1e293b">{log.admin_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{log.admin_role}</Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                bgcolor: actionStyle.bgColor,
                                borderRadius: 0.5,
                              }}
                            >
                              <Typography variant="caption" fontWeight={600} sx={{ color: actionStyle.color }}>
                                {log.action.replace(/_/g, ' ')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              {getCategoryIcon(log.category)}
                              <Typography variant="body2" color="#64748b" sx={{ textTransform: 'capitalize' }}>
                                {log.category}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" color="#1e293b" noWrap sx={{ maxWidth: 150 }}>
                              {log.target_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {log.status === 'success' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#16a34a' }}>
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption" fontWeight={500}>OK</Typography>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#dc2626' }}>
                                <ErrorIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption" fontWeight={500}>Erreur</Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Tooltip title={t('auditLogs.viewDetails')}>
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedLog(log); setDetailOpen(true); }}
                                sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                              >
                                <VisibilityIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 8 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ width: 64, height: 64, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                            <HistoryIcon sx={{ fontSize: 32, color: '#64748b' }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">{t('auditLogs.noLogs')}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {data && data.total > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={data.total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                labelRowsPerPage={t('common.rowsPerPage')}
                sx={{ borderTop: '1px solid #e2e8f0' }}
              />
            )}
          </>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {selectedLog && (
          <>
            <DialogTitle sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        bgcolor: getActionStyle(selectedLog.action).bgColor,
                        borderRadius: 0.5,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} sx={{ color: getActionStyle(selectedLog.action).color }}>
                        {selectedLog.action.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="#1e293b">
                      Détails de l'action
                    </Typography>
                  </Box>
                  <IconButton onClick={() => setDetailOpen(false)} size="small" sx={{ bgcolor: 'white' }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ p: 1, bgcolor: '#dbeafe', borderRadius: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('auditLogs.admin')}</Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">{selectedLog.admin_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{selectedLog.admin_role}</Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('auditLogs.dateTime')}</Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">{formatDate(selectedLog.created_at)}</Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 0.5 }}>
                      <CategoryIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('auditLogs.target')}</Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">
                        {selectedLog.target_name || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedLog.target_type} #{selectedLog.target_id}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 0.5 }}>
                      <ComputerIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('auditLogs.ipAddress')}</Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">{selectedLog.ip_address}</Typography>
                    </Box>
                  </Stack>
                </Grid>
                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} color="#64748b" sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                      {t('auditLogs.details')}
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: 12,
                        overflow: 'auto',
                        maxHeight: 200,
                      }}
                    >
                      <pre style={{ margin: 0 }}>
                        {JSON.stringify(selectedLog.details, null, 2)}
                      </pre>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setDetailOpen(false)} sx={{ borderRadius: 1 }}>{t('common.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AuditLogsTab;
